import React from "react";
import { useTable, Column, useSortBy, CellProps } from "react-table";
import { BsSortDown, BsSortUp, BsTrash, BsPencil } from "react-icons/bs";
import { Oval } from "react-loader-spinner";

import { Vehicle } from "../../../../models";
import { deriveStatus, isLowFuel } from "../../vehicleStatus";

import "./VehiclesTable.scss";

export interface VehiclesTableProps {
  data: Vehicle[];
  onEdit: (vehicle: Vehicle) => void;
  onDelete: (vehicle: Vehicle) => void;
  deletingId?: string;
}

const dash = (value: React.ReactNode): React.ReactNode =>
  value === undefined || value === null || value === "" ? "—" : value;

export function VehiclesTable(props: VehiclesTableProps) {
  const { data, onEdit, onDelete, deletingId } = props;

  const columns: Array<Column<Vehicle>> = React.useMemo(
    () => [
      {
        Header: "Vehicle",
        id: "vehicle",
        accessor: (v) => v.label,
        Cell: ({ row }: CellProps<Vehicle>) => {
          const v = row.original;
          const makeModel = [v.make, v.model].filter(Boolean).join(" ");
          const withYear = v.modelYear ? `${makeModel} (${v.modelYear})` : makeModel;
          const fuelLabel = v.fuelType
            ? v.fuelType.charAt(0).toUpperCase() + v.fuelType.slice(1)
            : undefined;
          const mpgLabel = v.mpg != null ? `${v.mpg} mpg` : undefined;
          const subtitle = [withYear, fuelLabel, mpgLabel].filter(Boolean).join(" · ");
          return (
            <div className="vehicle-cell">
              <div className="label">{v.label}</div>
              {subtitle ? <div className="subtitle">{subtitle}</div> : null}
            </div>
          );
        },
      },
      {
        Header: "Engine",
        id: "engine",
        accessor: (v) => v.stats?.engineState,
        Cell: ({ row }: CellProps<Vehicle>) => {
          const state = row.original.stats?.available ? row.original.stats.engineState : undefined;
          if (!state) return <span className="cell-dash">—</span>;
          return <span className={`engine-badge engine-${state.toLowerCase()}`}>{state}</span>;
        },
      },
      {
        Header: "Fuel",
        id: "fuel",
        accessor: (v) => v.stats?.fuelPercent ?? -1,
        Cell: ({ row }: CellProps<Vehicle>) => {
          const stats = row.original.stats;
          const pct = stats?.available ? stats.fuelPercent : undefined;
          return (
            <span className={`fuel-cell${isLowFuel(stats) ? " low-fuel" : ""}`}>
              {pct === undefined ? "—" : `${pct}%`}
            </span>
          );
        },
      },
      {
        Header: "Odometer",
        id: "odometer",
        accessor: (v) => v.stats?.odometerMiles ?? -1,
        Cell: ({ row }: CellProps<Vehicle>) => {
          const stats = row.original.stats;
          const miles = stats?.available ? stats.odometerMiles : undefined;
          return <>{miles === undefined ? "—" : `${Math.round(miles).toLocaleString()} mi`}</>;
        },
      },
      {
        Header: "Speed",
        id: "speed",
        accessor: (v) => v.location?.speedMph ?? -1,
        Cell: ({ row }: CellProps<Vehicle>) => {
          const loc = row.original.location;
          const mph = loc?.available ? loc.speedMph : undefined;
          return <>{mph === undefined ? "—" : `${Math.round(mph)} mph`}</>;
        },
      },
      {
        Header: "Location",
        id: "location",
        accessor: (v) => v.location?.address ?? "",
        Cell: ({ row }: CellProps<Vehicle>) => {
          const loc = row.original.location;
          const address = loc?.available ? loc.address : undefined;
          return (
            <span className="location-cell" title={address ?? undefined}>
              {dash(address)}
            </span>
          );
        },
      },
      {
        Header: "Plate",
        id: "plate",
        accessor: (v) => v.licensePlate ?? "",
        Cell: ({ row }: CellProps<Vehicle>) => (
          <span className="plate-cell" title={row.original.vin ?? undefined}>
            {dash(row.original.licensePlate)}
          </span>
        ),
      },
      {
        Header: "Status",
        id: "status",
        accessor: (v) => deriveStatus(v).label,
        Cell: ({ row }: CellProps<Vehicle>) => {
          const status = deriveStatus(row.original);
          return <span className={`status-cell status-${status.kind}`}>{status.label}</span>;
        },
      },
      {
        Header: "Actions",
        id: "actions",
        disableSortBy: true,
        Cell: ({ row }: CellProps<Vehicle>) => {
          const v = row.original;
          return (
            <div className="actions-cell">
              <BsPencil className="edit-icon" onClick={() => onEdit(v)} />
              {deletingId === v.id ? (
                <Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A" />
              ) : (
                <BsTrash className="delete-icon" onClick={() => onDelete(v)} />
              )}
            </div>
          );
        },
      },
    ],
    [onEdit, onDelete, deletingId]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data },
    useSortBy
  );

  return (
    <table {...getTableProps()} className="new-table vehicles-table">
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <th {...column.getHeaderProps((column as any).getSortByToggleProps())}>
                <div className="header-cell">
                  {column.render("Header")}
                  {(column as any).isSorted ? (
                    (column as any).isSortedDesc ? (
                      <BsSortDown className="sorting-icon" />
                    ) : (
                      <BsSortUp className="sorting-icon" />
                    )
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map((cell) => (
                <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
